import React, { useMemo, useState } from 'react';
import { Banner, Button, Card, Input, Select, Switch } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { CURRENCY_OPTIONS } from './settingsApi';

let _uid = 0;
const makeKey = () => `step-${Date.now()}-${_uid++}`;

const DOC_TYPES = [
  { key: 'requisition', label: 'Requisitions', field: 'requisitionApproval', approvePermission: 'requisitions.approve' },
  { key: 'purchaseOrder', label: 'Purchase orders', field: 'purchaseOrderApproval', approvePermission: 'purchaseorders.approve' },
  { key: 'invoice', label: 'Supplier invoices', field: 'invoiceApproval', approvePermission: 'invoices.approve' },
];

function normalizeConfig(cfg, fallbackCurrency) {
  return {
    allowSelfApproval: !!cfg?.allowSelfApproval,
    allowAdminOverride: cfg?.allowAdminOverride !== false,
    thresholdCurrency: cfg?.thresholdCurrency || fallbackCurrency || 'USD',
    reminderAfterHours: cfg?.reminderAfterHours ?? '',
    escalationAfterHours: cfg?.escalationAfterHours ?? '',
    escalationRoleId: cfg?.escalationRoleId || '',
    steps: (cfg?.steps || [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((s) => ({ key: makeKey(), roleId: s.roleId || '', minAmount: s.minAmount ?? 0 })),
  };
}

function normalize(settings, fallbackCurrency) {
  return {
    overrideApprovalWorkflows: !!settings?.overrideApprovalWorkflows,
    approvalNotificationsEnabled: settings?.approvalNotificationsEnabled !== false,
    requisition: normalizeConfig(settings?.requisitionApproval, fallbackCurrency),
    purchaseOrder: normalizeConfig(settings?.purchaseOrderApproval, fallbackCurrency),
    invoice: normalizeConfig(settings?.invoiceApproval, fallbackCurrency),
  };
}

export default function ApprovalWorkflowForm({ mode = 'org', initial, roles = [], fallbackCurrency, saving, onSave }) {
  const t = useT();
  const [form, setForm] = useState(() => normalize(initial, fallbackCurrency));
  const isProperty = mode === 'property';

  const roleOptions = useMemo(
    () => [{ value: '', label: t('Select a role…') }, ...roles.map((r) => ({ value: r.id, label: r.name }))],
    [roles, t],
  );
  const escalationRoleOptions = useMemo(
    () => [{ value: '', label: t('Organization admins (default)') }, ...roles.map((r) => ({ value: r.id, label: r.name }))],
    [roles, t],
  );
  const roleById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);

  const setDoc = (docKey, patch) =>
    setForm((f) => ({ ...f, [docKey]: { ...f[docKey], ...patch } }));

  const addStep = (docKey) =>
    setDoc(docKey, { steps: [...form[docKey].steps, { key: makeKey(), roleId: '', minAmount: 0 }] });
  const removeStep = (docKey, key) =>
    setDoc(docKey, { steps: form[docKey].steps.filter((s) => s.key !== key) });
  const setStep = (docKey, key, patch) =>
    setDoc(docKey, { steps: form[docKey].steps.map((s) => (s.key === key ? { ...s, ...patch } : s)) });

  const toHours = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  };

  const buildConfig = (doc) => ({
    allowSelfApproval: doc.allowSelfApproval,
    allowAdminOverride: doc.allowAdminOverride,
    thresholdCurrency: doc.thresholdCurrency || null,
    reminderAfterHours: toHours(doc.reminderAfterHours),
    escalationAfterHours: toHours(doc.escalationAfterHours),
    escalationRoleId: doc.escalationRoleId || null,
    escalationRoleName: doc.escalationRoleId ? roleById.get(doc.escalationRoleId)?.name || null : null,
    steps: doc.steps
      .filter((s) => s.roleId)
      .map((s, i) => ({
        order: i + 1,
        roleId: s.roleId,
        roleName: roleById.get(s.roleId)?.name || null,
        minAmount: Number(s.minAmount) || 0,
      })),
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      requisitionApproval: buildConfig(form.requisition),
      purchaseOrderApproval: buildConfig(form.purchaseOrder),
      invoiceApproval: buildConfig(form.invoice),
    };
    if (isProperty) payload.overrideApprovalWorkflows = form.overrideApprovalWorkflows;
    else payload.approvalNotificationsEnabled = form.approvalNotificationsEnabled;
    onSave(payload);
  };

  const showSections = !isProperty || form.overrideApprovalWorkflows;

  const renderDoc = (doc) => {
    const state = form[doc.key];
    return (
      <Card key={doc.key}>
        <Card.Body>
          <div className="set-section__head">
            <div>
              <h3 className="set-section__title">{t(doc.label)}</h3>
              <p className="set-section__hint">
                {t('Steps apply cumulatively, in order. A document requires every step whose threshold is at or below its total.')}
                {doc.key === 'invoice' && ` ${t('A price or quantity variance forces the full chain regardless of amount.')}`}
              </p>
            </div>
          </div>

          <div className="appr-steps">
            {state.steps.length === 0 && (
              <p className="set-inherit">{t('No approval steps — a single approval by anyone with the {permission} permission is required.', { permission: doc.approvePermission })}</p>
            )}
            {state.steps.map((s, i) => {
              const role = roleById.get(s.roleId);
              const missingPerm = role && !role.permissions?.includes('*') && !role.permissions?.includes(doc.approvePermission);
              return (
                <div key={s.key} className="appr-step">
                  <span className="appr-step__num">{i + 1}</span>
                  <div className="appr-step__role">
                    <Select
                      options={roleOptions}
                      value={s.roleId}
                      onChange={(v) => setStep(doc.key, s.key, { roleId: v })}
                      ariaLabel={t('Approver role')}
                      searchable
                    />
                    {missingPerm && (
                      <span className="appr-step__warn">
                        <Icon.CircleAlert size={12} /> {t('This role lacks the {permission} permission.', { permission: doc.approvePermission })}
                      </span>
                    )}
                  </div>
                  <div className="appr-step__amount">
                    <span className="appr-step__amount-prefix">≥ {state.thresholdCurrency}</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={s.minAmount}
                      onChange={(e) => setStep(doc.key, s.key, { minAmount: e.target.value })}
                      aria-label={t('Minimum amount')}
                    />
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeStep(doc.key, s.key)} aria-label={t('Remove step')}>
                    <Icon.Trash size={15} />
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="appr-doc__foot">
            <Button type="button" variant="secondary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={() => addStep(doc.key)}>
              {t('Add step')}
            </Button>
            <div className="appr-doc__opts">
              <div className="appr-doc__ccy">
                <span className="appr-doc__ccy-label">{t('Threshold currency')}</span>
                <Select
                  options={CURRENCY_OPTIONS}
                  value={state.thresholdCurrency}
                  onChange={(v) => setDoc(doc.key, { thresholdCurrency: v })}
                  ariaLabel={t('Threshold currency')}
                />
              </div>
              <Switch
                checked={state.allowSelfApproval}
                onChange={(v) => setDoc(doc.key, { allowSelfApproval: v })}
                label={t('Allow self-approval')}
              />
              <Switch
                checked={state.allowAdminOverride}
                onChange={(v) => setDoc(doc.key, { allowAdminOverride: v })}
                label={t('Admins can approve any step')}
              />
            </div>
          </div>

          <div className="appr-doc__notify">
            <p className="set-section__hint">
              {t('Reminders re-nudge the pending approvers; escalation also notifies the escalation target (the approval still needs the right role to act). Leave hours blank to disable.')}
            </p>
            <div className="appr-doc__notify-grid">
              <div className="appr-doc__notify-field">
                <span className="appr-doc__ccy-label">{t('Reminder after (hours)')}</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={state.reminderAfterHours}
                  onChange={(e) => setDoc(doc.key, { reminderAfterHours: e.target.value })}
                  aria-label={t('Reminder after hours')}
                  placeholder={t('Off')}
                />
              </div>
              <div className="appr-doc__notify-field">
                <span className="appr-doc__ccy-label">{t('Escalate after (hours)')}</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={state.escalationAfterHours}
                  onChange={(e) => setDoc(doc.key, { escalationAfterHours: e.target.value })}
                  aria-label={t('Escalate after hours')}
                  placeholder={t('Off')}
                />
              </div>
              <div className="appr-doc__notify-field">
                <span className="appr-doc__ccy-label">{t('Escalation target')}</span>
                <Select
                  options={escalationRoleOptions}
                  value={state.escalationRoleId}
                  onChange={(v) => setDoc(doc.key, { escalationRoleId: v })}
                  ariaLabel={t('Escalation target role')}
                  searchable
                />
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <form className="set-form" onSubmit={submit}>
      {roles.length === 0 && (
        <Banner tone="info">{t('No roles found. Create roles under Users & Roles before configuring approval steps.')}</Banner>
      )}

      {!isProperty && (
        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">{t('Approval emails')}</h3>
                <p className="set-section__hint">
                  {t('Master switch for all approval notifications (action-needed, reminders, escalation, decisions). Individual users can still opt out in My Account.')}
                </p>
              </div>
              <Switch
                checked={form.approvalNotificationsEnabled}
                onChange={(on) => setForm((f) => ({ ...f, approvalNotificationsEnabled: on }))}
                label={t('Send approval emails')}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {isProperty && (
        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">{t('Property override')}</h3>
                <p className="set-section__hint">{t('When off, this property inherits the organization approval workflows.')}</p>
              </div>
              <Switch
                checked={form.overrideApprovalWorkflows}
                onChange={(on) => {
                  // Seed from inherited config the first time the override is enabled.
                  if (on && form.requisition.steps.length === 0 && form.purchaseOrder.steps.length === 0 && form.invoice.steps.length === 0 && initial) {
                    setForm(normalize({ ...initial, overrideApprovalWorkflows: true }, fallbackCurrency));
                  } else {
                    setForm((f) => ({ ...f, overrideApprovalWorkflows: on }));
                  }
                }}
                label={t('Override')}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {showSections ? (
        DOC_TYPES.map(renderDoc)
      ) : (
        <Card><Card.Body><p className="set-inherit">{t('Inherits the organization approval workflows.')}</p></Card.Body></Card>
      )}

      <div className="set-form__actions">
        <Button type="submit" variant="primary" loading={saving}>{t('Save changes')}</Button>
      </div>
    </form>
  );
}
